import { durationEn, durationRu, durationSr, englishDuration, russianCount, russianDuration, serbianDuration, serbianForm } from './plural.ts';

export const LANGS = ['sr', 'en', 'ru'] as const;
export type Lang = (typeof LANGS)[number];

/**
 * Interface strings. All locales are one structure: a missing translation does not compile.
 * The server returns no human-readable text; an error only has a `code`, and the string is
 * looked up here by it.
 */
export type Strings = {
  // General
  retry: string;
  cancel: string;
  close: string;
  skip: string;
  delete: string;
  confirm: string;
  loading: string;
  send: string;
  seeAll: string;
  somethingWentWrong: string;
  // Tabs
  homeTab: string;
  catalogTab: string;
  searchTitle: string;
  searchHint: string;
  // Empty search title: the nav bar is already called "Search", so this is a question.
  searchEmptyTitle: string;
  ordersTitle: string;
  profileTitle: string;
  // Sign-in
  signInTitle: string;
  signInSubtitle: string;
  phoneLabel: string;
  phonePlaceholder: string;
  sendCode: string;
  codeTitle: string;
  codeSubtitle: (a: string) => string;
  codeSubtitleMessenger: (app: string, phone: string) => string;
  codeBySms: string;
  codeHint: string;
  resend: string;
  resendIn: (a: number) => string;
  /** Часовой лимит кодов исчерпан: через сколько минут можно снова. */
  otpThrottledFor: (minutes: number) => string;
  changeNumber: string;
  /** Подпись под входом: markdown-ссылки на условия и политику. */
  legalNote: (termsUrl: string, privacyUrl: string) => string;
  // Addresses
  chooseAddress: string;
  newAddress: string;
  searchAddressPlaceholder: string;
  noAddresses: string;
  noAddressesHint: string;
  entranceComment: string;
  entranceCommentPlaceholder: string;
  addressEntrance: string;
  addressFloor: string;
  addressApartment: string;
  addressIntercom: string;
  deliverHere: string;
  outOfZone: string;
  notDeliverable: string;
  outOfZoneHint: (a: string) => string;
  changeAddress: string;
  // Адрес вне зон: попросить сообщить о начале доставки и отметка, что просьба принята.
  coverageRequest: string;
  coverageRequested: string;
  locateMe: string;
  dragPinHint: string;
  myAddresses: string;
  // VoiceOver labels for icon-only controls
  a11yAddFavorite: string;
  a11yRemoveFavorite: string;
  a11yAddToCart: string;
  a11yQuantity: string;
  a11yIncrease: string;
  a11yDecrease: string;
  a11yRemoveFromCart: string;
  a11yNotifications: (unread: number) => string;
  a11ySendMessage: string;
  a11yClearText: string;
  a11yProductInfo: string;
  // Кнопка «поделиться» в карточке товара: в iOS её подписывает система.
  share: string;
  a11yStars: (a: number) => string;
  // Home and store
  openUntil: (closesAt: string, eta: string) => string;
  closedOpensAt: (a: string) => string;
  storeClosedTitle: string;
  storeClosedHint: (a: string) => string;
  // Order surge: intake stopped by the operator.
  storePausedLine: string;
  storePausedTitle: string;
  storePausedHint: string;
  // Order surge: still accepting, but the ETA is extended by N minutes.
  storeDelayedTitle: string;
  storeDelayedHint: (extraMinutes: number) => string;
  orderAgain: string;
  addAllToCart: string;
  addedToCart: string;
  youBought: string;
  homeProducts: string;
  homeSoldOut: string;
  homeSoldOutHint: string;
  purchasesEmptyHint: string;
  deals: string;
  exploreDeals: string;
  mostOrdered: string;
  newItems: string;
  badgeNew: string;
  badgeAge: string;
  ageGateTitle: string;
  ageGateText: string;
  ageGateConfirm: string;
  ageGateUnderage: string;
  birthDate: string;
  birthDateHint: string;
  emailForReceipts: string;
  emailForReceiptsHint: string;
  ageRestrictedOrder: string;
  itemsCount: (a: number) => string;
  // Storefront
  searchProducts: string;
  nothingFound: string;
  nothingFoundHint: string;
  // Subtitle above search results: how many products were found.
  resultsCount: (a: number) => string;
  categoryEmptyHint: string;
  outOfStock: string;
  onlyLeft: (a: number) => string;
  addToCart: string;
  goToCart: string;
  composition: string;
  nutrition: string;
  per100g: string;
  per100ml: string;
  energyLabel: string;
  proteinLabel: string;
  fatLabel: string;
  carbsLabel: string;
  kcalUnit: string;
  gramUnit: string;
  // Short macro labels for the one-line totals and the table header.
  proteinShort: string;
  fatShort: string;
  carbsShort: string;
  // Row labels of the nutrition table: a serving of a packaged product, the chosen amount of a weighed one.
  nutritionWhole: string;
  nutritionAmount: (a: string) => string;
  // Under the unfolded cart or order totals when some line has no weight or nutrition.
  nutritionPartial: string;
  tagVegan: string;
  tagGlutenFree: string;
  tagSugarFree: string;
  productInfo: string;
  readMore: string;
  gtin: string;
  countryOfOrigin: string;
  allergens: string;
  additives: string;
  // "Shelf life" row of the product info sheet and its value from a number of days.
  shelfLife: string;
  shelfLifeValue: (a: number) => string;
  storage: string;
  promoUntil: (a: string) => string;
  oftenBoughtWith: string;
  youMightLike: string;
  cartRecommendations: string;
  report: string;
  reportReason: (a: string) => string;
  reportPlaceholder: string;
  reportSend: string;
  reportSent: string;
  // Favorites
  favoritesTitle: string;
  favoritesEmpty: string;
  favoritesEmptyHint: string;
  // Cart
  cartTitle: string;
  cartEmpty: string;
  cartEmptyHint: string;
  backToCatalog: string;
  itemsTotal: string;
  deliveryFee: string;
  deliveryFree: string;
  deliveryFreeUnlocked: string;
  deliveryTime: string;
  etaAbout: (a: number) => string;
  freeFrom: (a: string) => string;
  addMoreForFreeDelivery: (a: string) => string;
  addMoreToOrder: (a: string) => string;
  /** Блокер корзины и оформления: вес заказа и лимит, оба уже отформатированы («33 kg»). */
  orderTooHeavy: (weight: string, limit: string) => string;
  // Short forms for the cart bar above the tab bar: the long phrase does not fit there.
  toFreeDeliveryShort: (a: string) => string;
  toMinimumShort: (a: string) => string;
  total: string;
  checkout: string;
  clearCart: string;
  // "Name: sold out": the product is no longer in stock.
  itemGone: string;
  // "Name: 2 left": the cart had more than the warehouse has.
  itemLeft: (a: number) => string;
  cartUpdated: string;
  cartUpdatedHint: string;
  fixCart: string;
  checkAgain: string;
  // Checkout
  checkoutTitle: string;
  deliveryTo: string;
  commentToCourier: string;
  commentPlaceholder: string;
  leaveAtDoor: string;
  leaveAtDoorHint: string;
  leaveAtDoorBlocked: string;
  alcoholClosedHint: (a: string, b: string) => string;
  testCard: string;
  payAmount: (a: string) => string;
  // Words before the Apple Pay logo on the pay button.
  payWith: string;
  // Markdown: the terms link goes to the URL passed in.
  checkoutLegalNote: (a: string) => string;
  paying: string;
  // Hosted payment page: the tile title, what happens on tap, the consent checkbox (markdown link), the marks caption.
  paymentCardTitle: string;
  vatIncluded: string;
  consentTerms: (a: string) => string;
  cardsAccepted: string;
  paymentChecking: string;
  paymentConfirmed: string;
  paymentStillChecking: string;
  // Карты: ввод в приложении, сохранённые карты, плашка хода оплаты.
  cardNumber: string;
  cardExpiry: string;
  cardOwner: string;
  newCard: string;
  paymentMethods: string;
  noCardsHint: string;
  addCard: string;
  saveCard: string;
  cardSaved: string;
  cardCheckHint: string;
  cardStillChecking: string;
  defaultCardHint: string;
  deleteCard: string;
  deleteCardTitle: string;
  deleteCardText: string;
  cardDeleted: string;
  edit: string;
  done: string;
  paidWithCard: string;
  paymentInProgress: string;
  paymentDone: string;
  paymentProblem: string;
  payOtherWay: string;
  promoCode: string;
  promoCodePlaceholder: string;
  promoApply: string;
  promoApplied: (a: string) => string;
  promoRemove: string;
  discount: string;
  gift: string;
  giftLine: (a: string) => string;
  // Promo code rejection reason: the `details.reason` key of the `promo_code_invalid` response.
  promoReason: (a: string) => string;
  // Partner interstitial (demo): Belgrade Waterfront after a paid order.
  // Offer line over the marina video.
  partnerPromoSubtitle: string;
  // Button to the partner site.
  partnerPromoCta: string;
  // Order
  reserveLeft: (a: string) => string;
  reserveExpired: string;
  deliveryCodeTitle: string;
  deliveryCodeHint: string;
  codePromptTitle: string;
  codePromptHint: string;
  codePromptDone: string;
  notificationDeliveryCode: string;
  notificationDeliveryCodeHint: string;
  pushDeliveryCode: string;
  pushDeliveryCodeBody: (code: string) => string;
  openOrder: string;
  leaveAtDoorOrder: string;
  orderItems: string;
  receipt: string;
  openReceipt: string;
  courierName: string;
  // The courier carries other orders in the same trip: how many stops come first, or that ours is first.
  courierRunAhead: (count: number) => string;
  courierRunFirst: (others: number) => string;
  mapExpand: string;
  mapLocate: string;
  cancelOrder: string;
  cancelOrderConfirm: string;
  cancelReasonPlaceholder: string;
  cancelNeedsOperator: string;
  // The same for an order already on its way.
  cancelNeedsOperatorOnTheWay: string;
  noOrders: string;
  noOrdersHint: string;
  // "Stiže oko 14:35": the promised arrival time of an active order.
  arrivesAround: (time: string) => string;
  // The promised time has passed, the order is still on its way.
  arrivesSoon: string;
  activeOrder: string;
  historyTitle: string;
  // Rating and tips
  deliveredCongrats: string;
  deliveredBy: (courier: string) => string;
  rateOrderTitle: string;
  tipDeclined: string;
  rateOrderHint: string;
  thanksForRating: string;
  tipsTitle: string;
  tipsHint: string;
  // Caption under "0%" in the tips row.
  noTipsShort: string;
  // Custom tip amount.
  tipOther: string;
  ratingCommentPlaceholder: string;
  // Support and credits
  supportTitle: string;
  supportOrder: string;
  supportEmpty: string;
  supportEmptyHint: string;
  supportPlaceholder: string;
  supportStaff: string;
  bonusBalance: string;
  bonusUsed: string;
  // Checkout: how much is on the account, under the bonus row.
  bonusOnAccount: (a: string) => string;
  bonusHint: string;
  // Notifications
  notificationsTitle: string;
  notificationsEmpty: string;
  notificationsEmptyHint: string;
  notifyOrders: string;
  notifyOrdersHint: string;
  notifyPromo: string;
  notifyPromoHint: string;
  // Rows under the bell: a support reply, a bonus, a free redelivery.
  notificationSupportReply: string;
  notificationCredit: (a: string) => string;
  notificationRedelivery: string;
  notificationRedeliveryHint: string;
  notificationZoneOpen: string;
  // Бонус, который начислила система: причина — код, текст — здесь.
  creditReasonOrderCancelled: string;
  creditReasonOrderAdjusted: string;
  notificationZoneOpenHint: string;
  // Not everything was picked: the row title, the card refund line, the missing units on an
  // order line and the refund total on the order.
  notificationShortPick: string;
  notificationShortPickRefund: (a: string) => string;
  itemMissing: (a: number) => string;
  refundedToCard: string;
  // Order total after the refund: what actually stayed charged.
  chargedTotal: string;
  // Part of a delivered order was returned: the row title and the returned units on a line.
  notificationReturnRefund: string;
  itemReturned: (a: number) => string;
  // Adding the cart to an order that is still being picked: the cart button and the sheet title
  // take the order moment ("Today, 12:40"), then the charge lines and the outcomes.
  addToOrder: (a: string) => string;
  supplementTitle: (a: string) => string;
  supplementCharge: string;
  supplementNewTotal: string;
  supplementPay: (a: string) => string;
  supplementAdd: string;
  supplementPicked: string;
  supplementDeclined: string;
  supplementDone: string;
  // Profile
  // Edit sheet: name and photo, both optional.
  editProfile: string;
  yourName: string;
  yourNamePlaceholder: string;
  nameOptionalHint: string;
  changePhoto: string;
  removePhoto: string;
  save: string;
  language: string;
  signOut: string;
  signOutConfirm: string;
  sessionExpired: string;
  deleteAccount: string;
  deleteAccountConfirm: string;
  // Cart without an address: caption and button leading to address selection.
  addAddressForDelivery: string;
  addAddress: string;
  // Guest mode: sign-in is asked for at checkout and in the profile.
  signIn: string;
  signInToContinue: string;
  guestProfileHint: string;
  // Address sheet at the first "add to cart".
  addressGateTitle: string;
  addressGateText: string;
  // Notification permission primer after the first order.
  pushPrimerTitle: string;
  pushPrimerText: string;
  pushPrimerAllow: string;
  pushPrimerLater: string;
  // Sign in with Apple and the phone number for the courier.
  orDivider: string;
  phoneForCourierTitle: string;
  phoneForCourierText: string;
  phoneOptional: string;
  // Under the disabled Apple Pay button in the release build.
  // Title of the payment method card at checkout.
  payment: string;
  // "Product info" sheet when no composition field is filled in.
  productInfoMissing: string;
  // Short form of the accessory hint: only the amount, the progress bar gives the meaning.
  amountMore: (a: string) => string;
  privacyPolicy: string;
  termsOfUse: string;

  // Statuses and errors: they arrive as a code, the text is looked up by it.
  statuses: Record<string, string>;
  // Short status names for tight places; a status without a short form is shown by its full name.
  statusesShort: Record<string, string>;
  errors: Record<string, string>;

  // Push в браузере: заголовки уведомлений строит service worker по данным сообщения.
  // Заказ без своей строки показывается названием статуса.
  pushOrder: Record<string, string>;
  pushSupport: string;
  pushRedelivery: (orderNumber: string) => string;
  pushZoneOpen: string;
  pushShortPick: (orderNumber: string, amount: string) => string;
  pushReturnRefund: (orderNumber: string, amount: string) => string;
};

const sr: Strings = {
  retry: "Pokušaj ponovo",
  cancel: "Odustani",
  close: "Zatvori",
  skip: "Preskoči",
  delete: "Obriši",
  confirm: "Potvrdi",
  loading: "Učitavanje",
  send: "Pošalji",
  seeAll: "Vidi sve",
  somethingWentWrong: "Nešto nije u redu. Pokušaj ponovo.",
  homeTab: "Početna",
  catalogTab: "Katalog",
  searchTitle: "Pretraga",
  searchHint: "Ukucaj šta tražiš: mleko, hleb, kafa…",
  searchEmptyTitle: "Šta tražiš?",
  ordersTitle: "Porudžbine",
  profileTitle: "Profil",
  signInTitle: "Namirnice za 15 minuta",
  signInSubtitle: "Unesi broj telefona, poslaćemo kod za prijavu.",
  phoneLabel: "Broj telefona",
  phonePlaceholder: "6X XXX XXXX",
  sendCode: "Pošalji kod",
  codeTitle: "Unesi kod",
  codeSubtitle: (phone) => `Poslali smo šestocifreni kod na ${phone}.`,
  codeSubtitleMessenger: (app, phone) => `Poslali smo šestocifreni kod u ${app} na ${phone}.`,
  codeBySms: 'Pošalji kod SMS-om',
  codeHint: "Kod važi 5 minuta.",
  resend: "Pošalji ponovo",
  resendIn: (seconds) => `Novi kod za ${seconds} s`,
  otpThrottledFor: (minutes) => `Novi kod možeš zatražiti za ${minutes} min.`,
  changeNumber: "Promeni broj",
  legalNote: (terms, privacy) => `Prijavom prihvataš [uslove korišćenja](${terms}) i [politiku privatnosti](${privacy}).`,
  chooseAddress: "Izaberi adresu",
  newAddress: "Nova adresa",
  searchAddressPlaceholder: "Ulica i broj",
  noAddresses: "Još nema adresa",
  noAddressesHint: "Dodaj adresu da vidiš šta možemo da donesemo.",
  entranceComment: "Napomena za kurira",
  entranceCommentPlaceholder: "Šifra kapije, orijentir",
  addressEntrance: "Ulaz",
  addressFloor: "Sprat",
  addressApartment: "Stan",
  addressIntercom: "Interfon",
  deliverHere: "Dostavi ovde",
  outOfZone: "Ovde još ne dostavljamo",
  notDeliverable: "Van zone dostave",
  outOfZoneHint: (address) => `Na adresu ${address} još ne dostavljamo. Izaberi drugu adresu ili dodaj novu.`,
  changeAddress: "Promeni adresu",
  coverageRequest: "Javite mi kad počnete",
  coverageRequested: "Javićemo vam",
  locateMe: "Lokacija",
  dragPinHint: "Pomeri mapu da tačno označiš ulaz.",
  myAddresses: "Adrese",
  a11yAddFavorite: "Dodaj u omiljene",
  a11yRemoveFavorite: "Ukloni iz omiljenih",
  a11yAddToCart: "Dodaj u korpu",
  a11yQuantity: "Količina",
  a11yIncrease: "Dodaj još jedan",
  a11yDecrease: "Ukloni jedan",
  a11yRemoveFromCart: "Ukloni iz korpe",
  a11yNotifications: (unread) => unread > 0 ? `Obaveštenja, nepročitanih: ${unread}` : "Obaveštenja",
  a11ySendMessage: "Pošalji poruku",
  a11yClearText: "Obriši tekst",
  a11yProductInfo: "Informacije o proizvodu",
  share: "Podeli",
  a11yStars: (a) => `${a} od 5 zvezdica`,
  openUntil: (closesAt, eta) => `Otvoreno do ${closesAt} · ${eta} min`,
  closedOpensAt: (opensAt) => `Zatvoreno · otvaramo u ${opensAt}`,
  storeClosedTitle: "Trenutno smo zatvoreni",
  storeClosedHint: (opensAt) => `Korpu možeš da spremiš već sad, a poručivanje kreće u ${opensAt}.`,
  storePausedLine: "Pauza · previše porudžbina",
  storePausedTitle: "Ne stižemo, pauza na kratko",
  storePausedHint: "Stiglo je više porudžbina nego što možemo da isporučimo. Korpu možeš da spremiš, a prijem nastavljamo čim se rastereti.",
  storeDelayedTitle: "Gužva je, dostava traje duže",
  storeDelayedHint: (extra) => `Stiglo je više porudžbina nego obično, pa dostava traje oko ${durationSr(extra)} duže. Hvala na strpljenju.`,
  orderAgain: "Poruči ponovo",
  addAllToCart: "Dodaj sve u korpu",
  addedToCart: "Dodato",
  youBought: "Kupovali ste",
  homeProducts: "Proizvodi",
  homeSoldOut: "Sve je rasprodato",
  homeSoldOutHint: "Već vozimo nove proizvode",
  purchasesEmptyHint: "Ovde će biti ono što si već kupovao, da ga uzmeš jednim dodirom.",
  deals: "Akcije",
  exploreDeals: "Pogledaj sve akcije",
  mostOrdered: "Najčešće se poručuje",
  newItems: "Novo",
  badgeNew: "NOVO",
  badgeAge: "18+",
  ageGateTitle: "Potvrdi godine",
  ageGateText: "Alkohol prodajemo samo punoletnima. Unesi datum rođenja, kurir će pri predaji tražiti dokument.",
  ageGateConfirm: "Potvrdi",
  ageGateUnderage: "Artikli 18+ su dostupni samo punoletnima.",
  birthDate: "Datum rođenja",
  birthDateHint: "Potreban je za kupovinu artikala 18+.",
  emailForReceipts: "E-mail za račune",
  emailForReceiptsHint: "Šaljemo potvrdu o plaćanju i fiskalni račun.",
  ageRestrictedOrder: "U porudžbini je artikal 18+: pripremi dokument za kurira",
  itemsCount: (count) => `${count} ${serbianForm(count, "artikal", "artikla", "artikala")}`,
  searchProducts: "Traži namirnice",
  nothingFound: "Nema rezultata",
  nothingFoundHint: "Proveri kako je napisano ili pogledaj druge kategorije.",
  resultsCount: (count) => `${count} ${serbianForm(count, "proizvod", "proizvoda", "proizvoda")}`,
  categoryEmptyHint: "Trenutno nema ništa na stanju. Pogledaj susedne kategorije.",
  outOfStock: "Nema na stanju",
  onlyLeft: (qty) => `Još ${qty}`,
  addToCart: "Dodaj u korpu",
  goToCart: "Idi u korpu",
  composition: "Sastav",
  nutrition: "Nutritivne vrednosti",
  per100g: "na 100 g",
  per100ml: "na 100 ml",
  energyLabel: "Energija",
  proteinLabel: "Proteini",
  fatLabel: "Masti",
  carbsLabel: "Ugljeni hidrati",
  kcalUnit: "kcal",
  gramUnit: "g",
  proteinShort: "P",
  fatShort: "M",
  carbsShort: "UH",
  nutritionWhole: "Ceo proizvod",
  nutritionAmount: (weight) => `za ${weight}`,
  nutritionPartial: "Deo artikala nema podatke, zbir je približan.",
  tagVegan: "Vegan",
  tagGlutenFree: "Bez glutena",
  tagSugarFree: "Bez šećera",
  productInfo: "Informacije o proizvodu",
  readMore: "Pročitaj više",
  gtin: "GTIN",
  countryOfOrigin: "Zemlja porekla",
  allergens: "Alergeni",
  additives: "Aditivi",
  shelfLife: "Rok trajanja",
  shelfLifeValue: (days) => serbianDuration(days),
  storage: "Čuvanje",
  promoUntil: (day) => `Akcija važi do ${day} ili do isteka zaliha.`,
  oftenBoughtWith: "Zajedno je ukusnije",
  youMightLike: "Možda ti se dopadne",
  cartRecommendations: "Nešto si zaboravio?",
  report: "Prijavi problem",
  reportReason: (code) => {
      switch (code) {
        case "wrong_info": return "Pogrešan opis ili cena";
        case "quality": return "Loš kvalitet";
        case "missing": return "Nešto nedostaje";
        default: return "Drugo";
      }
  },
  reportPlaceholder: "Opiši šta nije u redu",
  reportSend: "Pošalji prijavu",
  reportSent: "Prijava je poslata, hvala",
  favoritesTitle: "Omiljeno",
  favoritesEmpty: "Još nema omiljenih",
  favoritesEmptyHint: "Dodirni srce na proizvodu i biće ovde.",
  cartTitle: "Korpa",
  cartEmpty: "Korpa je prazna",
  cartEmptyHint: "Dodaj namirnice iz prodavnice.",
  backToCatalog: "U prodavnicu",
  itemsTotal: "Namirnice",
  deliveryFee: "Dostava",
  deliveryFree: "Besplatno",
  deliveryFreeUnlocked: "Dostava je besplatna",
  deliveryTime: "Vreme dostave",
  etaAbout: (minutes) => `≈ ${minutes} min`,
  freeFrom: (amount) => `Besplatna dostava od ${amount}`,
  addMoreForFreeDelivery: (amount) => `Dodaj još ${amount} za besplatnu dostavu`,
  addMoreToOrder: (amount) => `Dodaj još ${amount} do minimuma`,
  orderTooHeavy: (weight, limit) => `Porudžbina je preteška: ${weight}, najviše ${limit}`,
  toFreeDeliveryShort: (amount) => `${amount} do besplatne dostave`,
  toMinimumShort: (amount) => `Još ${amount} do minimuma`,
  total: "Ukupno",
  checkout: "Poruči",
  clearCart: "Isprazni korpu",
  itemGone: "nema više na stanju",
  itemLeft: (qty) => `ostalo je ${qty}`,
  cartUpdated: "Korpa je osvežena",
  cartUpdatedHint: "Deo artikala se u međuvremenu prodao. Proveri korpu i poruči ponovo.",
  fixCart: "Osveži korpu",
  checkAgain: "Poruči ponovo",
  checkoutTitle: "Porudžbina",
  deliveryTo: "Dostava na",
  commentToCourier: "Poruka kuriru",
  commentPlaceholder: "Pozovi kad stigneš",
  leaveAtDoor: "Ostavi ispred vrata",
  leaveAtDoorHint: "Kurir neće zvoniti.",
  leaveAtDoorBlocked: "U korpi je artikal 18+: kurir proverava dokument i predaje lično.",
  alcoholClosedHint: (from, until) => `Artikli 18+ se prodaju od ${from} do ${until}. Ukloni ih iz korpe ili poruči kasnije.`,
  testCard: "Testna kartica",
  payAmount: (amount) => `Plati ${amount}`,
  payWith: "Plati sa",
  checkoutLegalNote: (url) => `Poručivanjem prihvataš [uslove korišćenja](${url}).`,
  paying: "Plaćanje u toku",
  paymentCardTitle: "Platna kartica",
  vatIncluded: "PDV je uključen u cenu.",
  consentTerms: (url) => `Upoznat sam sa [uslovima korišćenja](${url}) i prihvatam ih.`,
  cardsAccepted: "Prihvatamo",
  paymentChecking: "Proveravamo plaćanje…",
  paymentConfirmed: "Plaćanje uspešno, porudžbina je primljena.",
  paymentStillChecking: "Banka još nije odgovorila. Status porudžbine će se ažurirati sam.",
  cardNumber: "Broj kartice",
  cardExpiry: "Važi do",
  cardOwner: "Ime na kartici",
  newCard: "Nova kartica",
  paymentMethods: "Načini plaćanja",
  noCardsHint: "Dodaj karticu da plaćaš jednim dodirom.",
  addCard: "Dodaj karticu",
  saveCard: "Sačuvaj karticu",
  cardSaved: "Kartica je sačuvana",
  cardCheckHint: "Banka rezerviše 1,00 RSD za proveru kartice i odmah ih oslobađa.",
  cardStillChecking: "Banka još nije odgovorila. Kartica će se pojaviti u listi čim odgovori.",
  defaultCardHint: "Kartica sa kvačicom se predlaže pri plaćanju.",
  deleteCard: "Obriši",
  deleteCardTitle: "Obrisati karticu?",
  deleteCardText: "Sledeći put ćeš uneti podatke kartice ponovo.",
  cardDeleted: "Kartica je obrisana",
  edit: "Izmeni",
  done: "Gotovo",
  paidWithCard: "Plaćeno karticom",
  paymentInProgress: "Plaćanje u toku…",
  paymentDone: "Plaćeno!",
  paymentProblem: "Problem sa plaćanjem, potrebna je tvoja pomoć",
  payOtherWay: "Plati drugačije",
  promoCode: "Promo kod",
  promoCodePlaceholder: "Unesi kod",
  promoApply: "Primeni",
  promoApplied: (amount) => `Kod primenjen: −${amount}`,
  promoRemove: "Ukloni",
  discount: "Popust",
  gift: "Poklon",
  giftLine: (name) => `Poklon: ${name}`,
  promoReason: (reason) => {
      switch (reason) {
        case "not_found": return "Takav kod ne postoji.";
        case "inactive": case "expired": return "Kod više ne važi.";
        case "not_started": return "Kod još ne važi.";
        case "min_order": return "Korpa je premala za ovaj kod.";
        case "limit_reached": return "Kod je iskorišćen.";
        case "client_limit": return "Već si iskoristio ovaj kod.";
        case "first_order_only": return "Kod važi samo za prvu porudžbinu.";
        default: return "Kod nije prihvaćen.";
      }
  },
  partnerPromoSubtitle: "Stanovi na obali Save. 0% kamate — 7 rata tokom izgradnje.",
  partnerPromoCta: "Pogledaj stanove",
  reserveLeft: (time) => `Rezervacija ističe za ${time}`,
  reserveExpired: "Rezervacija je istekla",
  deliveryCodeTitle: "Kod za preuzimanje",
  deliveryCodeHint: "Recite ga kuriru kada vam preda porudžbinu.",
  codePromptTitle: "Kurir je kod vas",
  codePromptHint: "Recite kuriru ovaj kod",
  codePromptDone: "Važi",
  notificationDeliveryCode: "Kurir je kod vas",
  notificationDeliveryCodeHint: "Recite mu kod za preuzimanje",
  pushDeliveryCode: "Kurir je kod vas",
  pushDeliveryCodeBody: (code) => `Kod za kurira: ${code}`,
  openOrder: "Otvori porudžbinu",
  leaveAtDoorOrder: "Ostavljamo ispred vrata",
  orderItems: "Sadržaj",
  receipt: "Fiskalni račun",
  openReceipt: "Otvori račun",
  courierName: "Kurir",
  courierRunAhead: (count) => count === 1 ? "Prvo dostavlja još jednu porudžbinu, pa tvoju" : `Prvo dostavlja još ${count} porudžbine, pa tvoju`,
  courierRunFirst: (others) => others === 1 ? "Tvoja je prva, nosi još jednu porudžbinu" : `Tvoja je prva, nosi još ${others} porudžbine`,
  mapExpand: "Mapa preko celog ekrana",
  mapLocate: "Prikaži kurira i adresu",
  cancelOrder: "Otkaži porudžbinu",
  cancelOrderConfirm: "Otkazati porudžbinu?",
  cancelReasonPlaceholder: "Razlog",
  cancelNeedsOperator: "Porudžbina se već pakuje. Javi se podršci da je otkažemo.",
  cancelNeedsOperatorOnTheWay: "Porudžbina je već na putu. Javi se podršci da je otkažemo.",
  noOrders: "Još nema porudžbina",
  noOrdersHint: "Prva porudžbina stiže za dvadesetak minuta.",
  arrivesAround: (time) => `Stiže oko ${time}`,
  arrivesSoon: "Stiže uskoro",
  activeOrder: "U toku",
  historyTitle: "Ranije",
  deliveredCongrats: "Prijatno!",
  deliveredBy: (courier) => `Dostavio kurir ${courier}`,
  rateOrderTitle: "Kako ti se dopala porudžbina?",
  tipDeclined: "Kartica je odbila napojnicu. Ocena je sačuvana — pokušaj ponovo ili smanji iznos.",
  rateOrderHint: "Oceni dostavu i ostavi bakšiš kuriru",
  thanksForRating: "Hvala na oceni",
  tipsTitle: "Bakšiš kuriru",
  tipsHint: "Kurir dobija ceo iznos bakšiša",
  noTipsShort: "bez",
  tipOther: "Drugo",
  ratingCommentPlaceholder: "Komentar (nije obavezno)",
  supportTitle: "Podrška",
  supportOrder: "Piši podršci o porudžbini",
  supportEmpty: "Piši nam",
  supportEmptyHint: "Odgovaramo u aplikaciji, obično u roku od par minuta.",
  supportPlaceholder: "Poruka",
  supportStaff: "Podrška",
  bonusBalance: "Bonusi",
  bonusUsed: "Bonusi",
  bonusOnAccount: (amount) => `Na računu ${amount}`,
  bonusHint: "Bonusi se sami oduzimaju od sledeće porudžbine.",
  notificationsTitle: "Obaveštenja",
  notificationsEmpty: "Još nema obaveštenja",
  notificationsEmptyHint: "Ovde ćeš videti kretanje porudžbine i akcije.",
  notifyOrders: "O porudžbinama",
  notifyOrdersHint: "Kurir je krenuo, stigao, porudžbina je isporučena",
  notifyPromo: "Akcije i preporuke",
  notifyPromoHint: "Popusti na ono što često kupuješ",
  notificationSupportReply: "Odgovor na vaše obraćanje",
  notificationCredit: (a) => `Dodali smo vam ${a} bonusa`,
  notificationRedelivery: "Šaljemo porudžbinu ponovo",
  notificationRedeliveryHint: "Besplatno, kao izvinjenje",
  notificationZoneOpen: "Sada dostavljamo i na vašu adresu",
  creditReasonOrderCancelled: "Vratili smo bonuse otkazane porudžbine",
  creditReasonOrderAdjusted: "Porudžbina je preračunata, bonusi su vraćeni",
  notificationZoneOpenHint: "Možete da naručite",
  notificationShortPick: "Nije bilo svega iz porudžbine",
  notificationShortPickRefund: (a) => `Vratili smo ${a} na karticu`,
  itemMissing: (a) => `nije bilo: ${a}`,
  refundedToCard: "Vraćeno na karticu",
  chargedTotal: "Ukupno naplaćeno",
  notificationReturnRefund: "Povraćaj dela porudžbine",
  itemReturned: (a) => `vraćeno: ${a}`,
  addToOrder: (a) => `Dodaj u porudžbinu · ${a}`,
  supplementTitle: (a) => `Porudžbina · ${a}`,
  supplementCharge: "Doplata",
  supplementNewTotal: "Novi iznos porudžbine",
  supplementPay: (a) => `Doplati ${a}`,
  supplementAdd: "Dodaj bez doplate",
  supplementPicked: "Porudžbina je već spakovana. Napravite novu porudžbinu.",
  supplementDeclined: "Kartica je odbila doplatu, porudžbina je ostala ista",
  supplementDone: "Dodato u porudžbinu",
  editProfile: "Profil",
  yourName: "Ime",
  yourNamePlaceholder: "Kako da te oslovimo",
  nameOptionalHint: "Nije obavezno. Kurir će ga videti u porudžbini.",
  changePhoto: "Promeni fotografiju",
  removePhoto: "Ukloni fotografiju",
  save: "Sačuvaj",
  language: "Jezik",
  signOut: "Odjavi se",
  signOutConfirm: "Odjaviti se sa ovog uređaja?",
  sessionExpired: "Sesija je istekla, prijavi se ponovo.",
  deleteAccount: "Obriši nalog",
  deleteAccountConfirm: "Nalog se briše odmah i ne može se vratiti. Porudžbine i fiskalni računi ostaju sačuvani jer ih zakon nalaže.",
  addAddressForDelivery: "Dodaj adresu za dostavu",
  addAddress: "Dodaj adresu",
  signIn: "Prijavi se",
  signInToContinue: "Prijavi se da nastaviš",
  guestProfileHint: "Porudžbine, omiljeno i adrese čuvamo uz tvoj broj telefona. Kod za prijavu stiže SMS-om ili u mesindžer.",
  addressGateTitle: "Gde da dostavimo?",
  addressGateText: "Ponuda i cena dostave zavise od adrese, zato je proveravamo pre prvog artikla.",
  pushPrimerTitle: "Javićemo kad kurir krene",
  pushPrimerText: "Status porudžbine i kod za preuzimanje stižu kao obaveštenja. Akcije šaljemo samo ako ih uključiš u profilu.",
  pushPrimerAllow: "Uključi obaveštenja",
  pushPrimerLater: "Kasnije",
  orDivider: "ili",
  phoneForCourierTitle: "Broj za kurira",
  phoneForCourierText: "Kurir te zove ako ne nađe ulaz. Možeš ostaviti prazno.",
  phoneOptional: "Nije obavezno",
  payment: "Plaćanje",
  productInfoMissing: "Podaci o sastavu još nisu uneti.",
  amountMore: (amount) => `Još ${amount}`,
  privacyPolicy: "Politika privatnosti",
  termsOfUse: "Uslovi korišćenja",
  statuses: {
    "PAYMENT_PENDING": "Čeka plaćanje",
    "PAID": "Pakujemo",
    "PICKING": "Pakujemo",
    "PICKED": "Spakovana",
    "IN_TRANSIT": "Na putu do tebe",
    "DELIVERED": "Isporučena",
    "CANCELLED": "Otkazana",
    "CANCELLED_LATE": "Otkazana",
    "RETURN": "Povraćaj",
    "REFUNDED": "Novac vraćen",
  },
  statusesShort: {
    "PAYMENT_PENDING": "Čeka plaćanje",
    "IN_TRANSIT": "Na putu",
  },
  errors: {
    "offline": "Nema veze sa internetom.",
    "malformed_response": "Server je odgovorio neočekivano.",
    "invalid_phone": "Broj telefona nije ispravan.",
    "otp_invalid": "Kod nije tačan ili je istekao.",
    "otp_throttled": "Sačekaj malo pre novog koda.",
    "otp_attempts_exceeded": "Previše pokušaja. Zatraži novi kod.",
    "validation_failed": "Proveri unete podatke.",
    "address_required": "Izaberi adresu dostave.",
    "address_out_of_zone": "Na ovu adresu još ne dostavljamo.",
    "address_not_found": "Adresa nije pronađena.",
    "point_required": "Izaberi tačku na mapi.",
    "product_unavailable": "Nekog artikla više nema u prodaji.",
    "insufficient_stock": "Nema dovoljno na stanju.",
    "supplement_pending": "Doplata za prethodno dodate artikle je još u toku.",
    "promo_code_invalid": "Promo kod nije prihvaćen.",
    "order_too_small": "Porudžbina je ispod minimuma.",
    "order_limit": "Porudžbina je prevelika: smanji količinu ili podeli na dve porudžbine.",
    "sms_failed": "SMS nije poslat. Pokušaj ponovo.",
    "rate_limited": "Previše pokušaja. Sačekaj malo.",
    "empty_order": "Korpa je prazna.",
    "order_not_found": "Porudžbina nije pronađena.",
    "order_state": "Ovo se u trenutnom statusu ne može uraditi.",
    "invalid_transition": "Ovo se u trenutnom statusu ne može uraditi.",
    "cancel_needs_operator": "Porudžbina se već pakuje. Javi se podršci.",
    "store_closed": "Trenutno smo zatvoreni. Korpa te čeka do otvaranja.",
    "store_paused": "Prijem je na pauzi: stiglo je više porudžbina nego što možemo da isporučimo. Probaj malo kasnije.",
    "age_restricted": "Artikli 18+ su dostupni samo punoletnima.",
    "leave_at_door_not_allowed": "Porudžbina sa artiklom 18+ se ne ostavlja ispred vrata.",
    "alcohol_sale_closed": "Artikli 18+ se sada ne prodaju.",
    "birth_date_invalid": "Datum rođenja nije ispravan.",
    "email_invalid": "E-mail adresa nije ispravna.",
    "account_has_active_orders": "Imaš porudžbinu u toku: sačekaj da se završi ili je otkaži.",
    "call_in_progress": "Obrada je u toku. Pokušaj za koji trenutak.",
    "payment_declined": "Plaćanje nije prošlo.",
    "collection_not_found": "Ova ponuda više nije aktivna.",
    "product_not_found": "Proizvod nije pronađen.",
    "forbidden": "Nemaš pristup ovoj radnji.",
    "internal": "Greška na serveru. Pokušaj ponovo.",
  },
  pushOrder: {
    "PAID": "Pakujemo vašu porudžbinu",
    "PICKING": "Pakujemo vašu porudžbinu",
    "IN_TRANSIT": "Kurir je krenuo",
    "DELIVERED": "Porudžbina je isporučena. Ocenite je!",
    "CANCELLED": "Porudžbina je otkazana",
    "CANCELLED_LATE": "Porudžbina je otkazana",
    "REFUNDED": "Novac za porudžbinu je vraćen",
  },
  pushSupport: "Odgovor na vaše obraćanje",
  pushRedelivery: (orderNumber) => `Porudžbinu #${orderNumber} šaljemo ponovo, besplatno`,
  pushZoneOpen: "Sada dostavljamo i na vašu adresu — možete da naručite",
  pushShortPick: (orderNumber, amount) => `Porudžbina #${orderNumber}: nije bilo svega, vraćamo ${amount} na karticu`,
  pushReturnRefund: (orderNumber, amount) => `Porudžbina #${orderNumber}: vraćamo ${amount} na karticu za vraćenu robu`,
};

const en: Strings = {
  retry: "Try again",
  cancel: "Cancel",
  close: "Close",
  skip: "Skip",
  delete: "Delete",
  confirm: "Confirm",
  loading: "Loading",
  send: "Send",
  seeAll: "See all",
  somethingWentWrong: "Something went wrong. Try again.",
  homeTab: "Home",
  catalogTab: "Catalog",
  searchTitle: "Search",
  searchHint: "Type what you need: milk, bread, coffee…",
  searchEmptyTitle: "What are you looking for?",
  ordersTitle: "Orders",
  profileTitle: "Profile",
  signInTitle: "Groceries in 15 minutes",
  signInSubtitle: "Enter your phone number and we'll text you a code.",
  phoneLabel: "Phone number",
  phonePlaceholder: "6X XXX XXXX",
  sendCode: "Send code",
  codeTitle: "Enter the code",
  codeSubtitle: (phone) => `We sent a six-digit code to ${phone}.`,
  codeSubtitleMessenger: (app, phone) => `We sent a six-digit code to ${app} at ${phone}.`,
  codeBySms: 'Get the code by SMS',
  codeHint: "The code is valid for 5 minutes.",
  resend: "Send again",
  resendIn: (seconds) => `New code in ${seconds}s`,
  otpThrottledFor: (minutes) => `You can request a new code in ${minutes} min.`,
  changeNumber: "Change number",
  legalNote: (terms, privacy) => `By signing in you accept the [terms of use](${terms}) and the [privacy policy](${privacy}).`,
  chooseAddress: "Choose an address",
  newAddress: "New address",
  searchAddressPlaceholder: "Street and number",
  noAddresses: "No addresses yet",
  noAddressesHint: "Add an address to see what we can bring you.",
  entranceComment: "Note for the courier",
  entranceCommentPlaceholder: "Gate code, landmark",
  addressEntrance: "Entrance",
  addressFloor: "Floor",
  addressApartment: "Flat",
  addressIntercom: "Intercom",
  deliverHere: "Deliver here",
  outOfZone: "We don't deliver here yet",
  notDeliverable: "Outside the delivery zone",
  outOfZoneHint: (address) => `We don't deliver to ${address} yet. Pick another address or add a new one.`,
  changeAddress: "Change address",
  coverageRequest: "Tell me when you start",
  coverageRequested: "We'll let you know",
  locateMe: "Location",
  dragPinHint: "Move the map to mark the entrance precisely.",
  myAddresses: "Addresses",
  a11yAddFavorite: "Add to favourites",
  a11yRemoveFavorite: "Remove from favourites",
  a11yAddToCart: "Add to cart",
  a11yQuantity: "Quantity",
  a11yIncrease: "Add one more",
  a11yDecrease: "Remove one",
  a11yRemoveFromCart: "Remove from cart",
  a11yNotifications: (unread) => unread > 0 ? `Notifications, ${unread} unread` : "Notifications",
  a11ySendMessage: "Send message",
  a11yClearText: "Clear text",
  a11yProductInfo: "Product information",
  share: "Share",
  a11yStars: (a) => `${a} of 5 stars`,
  openUntil: (closesAt, eta) => `Open until ${closesAt} · ${eta} min`,
  closedOpensAt: (opensAt) => `Closed · opens at ${opensAt}`,
  storeClosedTitle: "We're closed right now",
  storeClosedHint: (opensAt) => `You can build your cart now; ordering opens at ${opensAt}.`,
  storePausedLine: "Paused · too many orders",
  storePausedTitle: "We can't keep up, short pause",
  storePausedHint: "More orders came in than we can deliver right now. You can build your cart; ordering resumes as soon as we catch up.",
  storeDelayedTitle: "Busy hour, delivery takes longer",
  storeDelayedHint: (extra) => `More orders than usual came in, so delivery takes about ${durationEn(extra)} longer. Thanks for your patience.`,
  orderAgain: "Order again",
  addAllToCart: "Add all to cart",
  addedToCart: "Added",
  youBought: "Recently bought",
  homeProducts: "Products",
  homeSoldOut: "Everything is sold out",
  homeSoldOutHint: "New products are already on the way",
  purchasesEmptyHint: "Things you've bought before will show up here for one-tap reorders.",
  deals: "Deals",
  exploreDeals: "Explore deals",
  mostOrdered: "Most ordered",
  newItems: "New",
  badgeNew: "NEW",
  badgeAge: "18+",
  ageGateTitle: "Confirm your age",
  ageGateText: "We sell alcohol to adults only. Enter your date of birth; the courier will ask for an ID at handover.",
  ageGateConfirm: "Confirm",
  ageGateUnderage: "18+ products are available to adults only.",
  birthDate: "Date of birth",
  birthDateHint: "Needed to buy 18+ products.",
  emailForReceipts: "Email for receipts",
  emailForReceiptsHint: "We send the payment confirmation and the fiscal receipt.",
  ageRestrictedOrder: "The order has an 18+ product: have your ID ready for the courier",
  itemsCount: (count) => count === 1 ? "1 item" : `${count} items`,
  searchProducts: "Search groceries",
  nothingFound: "No results",
  nothingFoundHint: "Check the spelling or browse other categories.",
  resultsCount: (count) => count === 1 ? "1 product" : `${count} products`,
  categoryEmptyHint: "Nothing in stock right now. Try a neighbouring category.",
  outOfStock: "Out of stock",
  onlyLeft: (qty) => `${qty} left`,
  addToCart: "Add to cart",
  goToCart: "Go to cart",
  composition: "Ingredients",
  nutrition: "Nutrition facts",
  per100g: "per 100 g",
  per100ml: "per 100 ml",
  energyLabel: "Energy",
  proteinLabel: "Protein",
  fatLabel: "Fat",
  carbsLabel: "Carbs",
  kcalUnit: "kcal",
  gramUnit: "g",
  proteinShort: "P",
  fatShort: "F",
  carbsShort: "C",
  nutritionWhole: "Whole pack",
  nutritionAmount: (weight) => `for ${weight}`,
  nutritionPartial: "Some items have no data, the total is approximate.",
  tagVegan: "Vegan",
  tagGlutenFree: "Gluten free",
  tagSugarFree: "Sugar free",
  productInfo: "Product info",
  readMore: "Read more",
  gtin: "GTIN",
  countryOfOrigin: "Country of origin",
  allergens: "Allergens",
  additives: "Additives",
  shelfLife: "Shelf life",
  shelfLifeValue: (days) => englishDuration(days),
  storage: "Storage",
  promoUntil: (day) => `The sale is valid until ${day} or while stocks last.`,
  oftenBoughtWith: "Better together",
  youMightLike: "You might also like",
  cartRecommendations: "Forgot something?",
  report: "Report",
  reportReason: (code) => {
      switch (code) {
        case "wrong_info": return "Wrong description or price";
        case "quality": return "Poor quality";
        case "missing": return "Something is missing";
        default: return "Other";
      }
  },
  reportPlaceholder: "Describe what went wrong",
  reportSend: "Send report",
  reportSent: "Report sent, thank you",
  favoritesTitle: "Favorites",
  favoritesEmpty: "No favorites yet",
  favoritesEmptyHint: "Tap the heart on a product and it will land here.",
  cartTitle: "Cart",
  cartEmpty: "Cart is empty",
  cartEmptyHint: "Add groceries from the store.",
  backToCatalog: "Go to store",
  itemsTotal: "Groceries",
  deliveryFee: "Delivery",
  deliveryFree: "Free",
  deliveryFreeUnlocked: "Delivery is free",
  deliveryTime: "Delivery time",
  etaAbout: (minutes) => `≈ ${minutes} min`,
  freeFrom: (amount) => `Free delivery from ${amount}`,
  addMoreForFreeDelivery: (amount) => `Add ${amount} more for free delivery`,
  addMoreToOrder: (amount) => `Add ${amount} more to reach the minimum`,
  orderTooHeavy: (weight, limit) => `The order is too heavy: ${weight}, the limit is ${limit}`,
  toFreeDeliveryShort: (amount) => `${amount} to free delivery`,
  toMinimumShort: (amount) => `${amount} to the minimum`,
  total: "Total",
  checkout: "Checkout",
  clearCart: "Empty the cart",
  itemGone: "is out of stock",
  itemLeft: (qty) => `left: ${qty}`,
  cartUpdated: "Cart updated",
  cartUpdatedHint: "Some items sold out while you were shopping. Check the cart and order again.",
  fixCart: "Update the cart",
  checkAgain: "Order again",
  checkoutTitle: "Order",
  deliveryTo: "Deliver to",
  commentToCourier: "Message for the courier",
  commentPlaceholder: "Call me when you arrive",
  leaveAtDoor: "Leave at the door",
  leaveAtDoorHint: "The courier will not ring.",
  leaveAtDoorBlocked: "The cart has an 18+ product: the courier checks your ID and hands it over in person.",
  alcoholClosedHint: (from, until) => `18+ products are sold from ${from} to ${until}. Remove them from the cart or order later.`,
  testCard: "Test card",
  payAmount: (amount) => `Pay ${amount}`,
  payWith: "Pay with",
  checkoutLegalNote: (url) => `By ordering you accept the [terms of use](${url}).`,
  paying: "Payment in progress",
  paymentCardTitle: "Payment card",
  vatIncluded: "VAT is included in the price.",
  consentTerms: (url) => `I have read the [terms of use](${url}) and accept them.`,
  cardsAccepted: "We accept",
  paymentChecking: "Checking the payment…",
  paymentConfirmed: "Payment successful, the order is in.",
  paymentStillChecking: "The bank has not answered yet. The order status will update by itself.",
  cardNumber: "Card number",
  cardExpiry: "Expiry",
  cardOwner: "Name on card",
  newCard: "New card",
  paymentMethods: "Payment methods",
  noCardsHint: "Add a card to pay with one tap.",
  addCard: "Add card",
  saveCard: "Save card",
  cardSaved: "Card saved",
  cardCheckHint: "The bank holds 1.00 RSD to check the card and releases it at once.",
  cardStillChecking: "The bank has not answered yet. The card appears in the list as soon as it does.",
  defaultCardHint: "The ticked card is offered at checkout.",
  deleteCard: "Delete",
  deleteCardTitle: "Delete the card?",
  deleteCardText: "Next time you will enter the card details again.",
  cardDeleted: "Card deleted",
  edit: "Edit",
  done: "Done",
  paidWithCard: "Paid with card",
  paymentInProgress: "Payment in progress…",
  paymentDone: "Paid!",
  paymentProblem: "Payment problem, we need your help",
  payOtherWay: "Pay another way",
  promoCode: "Promo code",
  promoCodePlaceholder: "Enter a code",
  promoApply: "Apply",
  promoApplied: (amount) => `Code applied: −${amount}`,
  promoRemove: "Remove",
  discount: "Discount",
  gift: "Gift",
  giftLine: (name) => `Gift: ${name}`,
  promoReason: (reason) => {
      switch (reason) {
        case "not_found": return "No such code.";
        case "inactive": case "expired": return "This code is no longer valid.";
        case "not_started": return "This code is not active yet.";
        case "min_order": return "The cart is too small for this code.";
        case "limit_reached": return "This code has been used up.";
        case "client_limit": return "You have already used this code.";
        case "first_order_only": return "This code is for the first order only.";
        default: return "The code was not accepted.";
      }
  },
  partnerPromoSubtitle: "Apartments on the Sava bank. 0% interest — 7 installments during construction.",
  partnerPromoCta: "See the apartments",
  reserveLeft: (time) => `Reservation expires in ${time}`,
  reserveExpired: "The reservation has expired",
  deliveryCodeTitle: "Pick-up code",
  deliveryCodeHint: "Tell it to the courier when they hand you the order.",
  codePromptTitle: "The courier is at your door",
  codePromptHint: "Tell the courier this code",
  codePromptDone: "Got it",
  notificationDeliveryCode: "The courier is at your door",
  notificationDeliveryCodeHint: "Tell them the pick-up code",
  pushDeliveryCode: "The courier is at your door",
  pushDeliveryCodeBody: (code) => `Code for the courier: ${code}`,
  openOrder: "Open order",
  leaveAtDoorOrder: "We leave it at the door",
  orderItems: "Items",
  receipt: "Fiscal receipt",
  openReceipt: "Open receipt",
  courierName: "Courier",
  courierRunAhead: (count) => count === 1 ? "Delivers one more order first, then yours" : `Delivers ${count} more orders first, then yours`,
  courierRunFirst: (others) => others === 1 ? "Yours is first, one more order in the bag" : `Yours is first, ${others} more orders in the bag`,
  mapExpand: "Full-screen map",
  mapLocate: "Show the courier and the address",
  cancelOrder: "Cancel order",
  cancelOrderConfirm: "Cancel this order?",
  cancelReasonPlaceholder: "Reason",
  cancelNeedsOperator: "The order is already being picked. Contact support to cancel it.",
  cancelNeedsOperatorOnTheWay: "The order is already on its way. Contact support to cancel it.",
  noOrders: "No orders yet",
  noOrdersHint: "The first one arrives in about twenty minutes.",
  arrivesAround: (time) => `Arrives around ${time}`,
  arrivesSoon: "Arriving soon",
  activeOrder: "In progress",
  historyTitle: "Earlier",
  deliveredCongrats: "Enjoy!",
  deliveredBy: (courier) => `Delivered by ${courier}`,
  rateOrderTitle: "How was your order?",
  tipDeclined: "The card declined the tip. Your rating is saved: try again or lower the amount.",
  rateOrderHint: "Rate the delivery and tip the courier",
  thanksForRating: "Thanks for rating",
  tipsTitle: "Tip the courier",
  tipsHint: "The courier gets the whole tip",
  noTipsShort: "none",
  tipOther: "Other",
  ratingCommentPlaceholder: "Comment (optional)",
  supportTitle: "Support",
  supportOrder: "Message support about the order",
  supportEmpty: "Write to us",
  supportEmptyHint: "We answer in the app, usually within a few minutes.",
  supportPlaceholder: "Message",
  supportStaff: "Support",
  bonusBalance: "Bonuses",
  bonusUsed: "Bonuses",
  bonusOnAccount: (amount) => `${amount} on your account`,
  bonusHint: "Bonuses are deducted from your next order automatically.",
  notificationsTitle: "Notifications",
  notificationsEmpty: "No notifications yet",
  notificationsEmptyHint: "Order updates and deals will show up here.",
  notifyOrders: "Order updates",
  notifyOrdersHint: "Courier on the way, arrived, order delivered",
  notifyPromo: "Deals and picks",
  notifyPromoHint: "Discounts on what you buy often",
  notificationSupportReply: "A reply to your request",
  notificationCredit: (a) => `We added ${a} in bonuses`,
  notificationRedelivery: "We're sending your order again",
  notificationRedeliveryHint: "Free of charge, as an apology",
  notificationZoneOpen: "We now deliver to your address",
  creditReasonOrderCancelled: "Bonuses from the cancelled order are back",
  creditReasonOrderAdjusted: "The order was recalculated, bonuses are back",
  notificationZoneOpenHint: "You can place an order",
  notificationShortPick: "Some items were out of stock",
  notificationShortPickRefund: (a) => `We returned ${a} to your card`,
  itemMissing: (a) => `missing: ${a}`,
  refundedToCard: "Refunded to card",
  chargedTotal: "Total charged",
  notificationReturnRefund: "Part of your order was returned",
  itemReturned: (a) => `returned: ${a}`,
  addToOrder: (a) => `Add to order · ${a}`,
  supplementTitle: (a) => `Order · ${a}`,
  supplementCharge: "To pay",
  supplementNewTotal: "New order total",
  supplementPay: (a) => `Pay ${a}`,
  supplementAdd: "Add without extra charge",
  supplementPicked: "The order is already packed. Place a new order.",
  supplementDeclined: "The card declined the charge, the order stays as it was",
  supplementDone: "Added to your order",
  editProfile: "Profile",
  yourName: "Name",
  yourNamePlaceholder: "What should we call you",
  nameOptionalHint: "Optional. The courier sees it on the order.",
  changePhoto: "Change photo",
  removePhoto: "Remove photo",
  save: "Save",
  language: "Language",
  signOut: "Sign out",
  signOutConfirm: "Sign out on this device?",
  sessionExpired: "Your session has expired, please sign in again.",
  deleteAccount: "Delete account",
  deleteAccountConfirm: "The account is deleted immediately and can't be restored. Orders and fiscal receipts are kept as required by law.",
  addAddressForDelivery: "Add a delivery address",
  addAddress: "Add address",
  signIn: "Sign in",
  signInToContinue: "Sign in to continue",
  guestProfileHint: "Orders, favorites and addresses are kept with your phone number. The sign-in code arrives by SMS or in a messenger.",
  addressGateTitle: "Where do we deliver?",
  addressGateText: "The shelf and the delivery fee depend on the address, so we check it before the first item.",
  pushPrimerTitle: "We'll tell you when the courier leaves",
  pushPrimerText: "Order status and the handover code arrive as notifications. Promos only if you turn them on in the profile.",
  pushPrimerAllow: "Turn on notifications",
  pushPrimerLater: "Later",
  orDivider: "or",
  phoneForCourierTitle: "Phone for the courier",
  phoneForCourierText: "The courier calls if they can't find the entrance. You can leave it empty.",
  phoneOptional: "Optional",
  payment: "Payment",
  productInfoMissing: "Ingredient details aren't available yet.",
  amountMore: (amount) => `${amount} more`,
  privacyPolicy: "Privacy policy",
  termsOfUse: "Terms of use",
  statuses: {
    "PAYMENT_PENDING": "Awaiting payment",
    "PAID": "Packing",
    "PICKING": "Picking",
    "PICKED": "Packed",
    "IN_TRANSIT": "On the way",
    "DELIVERED": "Delivered",
    "CANCELLED": "Cancelled",
    "CANCELLED_LATE": "Cancelled",
    "RETURN": "Return",
    "REFUNDED": "Refunded",
  },
  statusesShort: {
    "PAYMENT_PENDING": "Awaiting payment",
    "IN_TRANSIT": "On the way",
  },
  errors: {
    "offline": "No internet connection.",
    "malformed_response": "The server replied unexpectedly.",
    "invalid_phone": "That phone number isn't valid.",
    "otp_invalid": "The code is wrong or expired.",
    "otp_throttled": "Wait a moment before asking for a new code.",
    "otp_attempts_exceeded": "Too many attempts. Request a new code.",
    "validation_failed": "Check the details you entered.",
    "address_required": "Choose a delivery address.",
    "address_out_of_zone": "We don't deliver to this address yet.",
    "address_not_found": "Address not found.",
    "point_required": "Pick a point on the map.",
    "product_unavailable": "One of the items is no longer on sale.",
    "insufficient_stock": "Not enough in stock.",
    "supplement_pending": "The payment for items added earlier is still in progress.",
    "promo_code_invalid": "The promo code was not accepted.",
    "account_has_active_orders": "You have an order in progress: wait for it to finish or cancel it.",
    "order_too_small": "The order is below the minimum.",
    "order_limit": "The order is too big: reduce the quantity or split it into two orders.",
    "sms_failed": "The SMS was not sent. Try again.",
    "rate_limited": "Too many attempts. Wait a bit.",
    "empty_order": "The cart is empty.",
    "order_not_found": "Order not found.",
    "order_state": "That isn't possible in the current status.",
    "invalid_transition": "That isn't possible in the current status.",
    "cancel_needs_operator": "The order is already being picked. Contact support.",
    "store_closed": "We're closed right now. The cart will wait until we open.",
    "store_paused": "Ordering is paused: more orders came in than we can deliver. Please try again a little later.",
    "age_restricted": "18+ products are available to adults only.",
    "leave_at_door_not_allowed": "An order with an 18+ product cannot be left at the door.",
    "alcohol_sale_closed": "18+ products are not sold right now.",
    "birth_date_invalid": "The date of birth is not valid.",
    "email_invalid": "The email address is not valid.",
    "call_in_progress": "Processing. Try again in a moment.",
    "payment_declined": "The payment did not go through.",
    "collection_not_found": "This offer is no longer active.",
    "product_not_found": "Product not found.",
    "forbidden": "You don't have access to this action.",
    "internal": "Server error. Try again.",
  },
  pushOrder: {
    "PAID": "We're packing your order",
    "PICKING": "We're packing your order",
    "IN_TRANSIT": "The courier is on the way",
    "DELIVERED": "Order delivered. Rate it!",
    "CANCELLED": "Your order was cancelled",
    "CANCELLED_LATE": "Your order was cancelled",
    "REFUNDED": "Your order was refunded",
  },
  pushSupport: "A reply to your request",
  pushRedelivery: (orderNumber) => `We're sending order #${orderNumber} again, free of charge`,
  pushZoneOpen: "We now deliver to your address — you can place an order",
  pushShortPick: (orderNumber, amount) => `Order #${orderNumber}: some items were missing, ${amount} goes back to your card`,
  pushReturnRefund: (orderNumber, amount) => `Order #${orderNumber}: ${amount} goes back to your card for the returned items`,
};

const ru: Strings = {
  retry: "Повторить",
  cancel: "Отмена",
  close: "Закрыть",
  skip: "Пропустить",
  delete: "Удалить",
  confirm: "Подтвердить",
  loading: "Загрузка",
  send: "Отправить",
  seeAll: "Все",
  somethingWentWrong: "Что-то пошло не так. Попробуйте ещё раз.",
  homeTab: "Главная",
  catalogTab: "Каталог",
  searchTitle: "Поиск",
  searchHint: "Например: молоко, хлеб, кофе…",
  searchEmptyTitle: "Что ищем?",
  ordersTitle: "Заказы",
  profileTitle: "Профиль",
  signInTitle: "Продукты за 15 минут",
  signInSubtitle: "Введите номер телефона, мы пришлём код.",
  phoneLabel: "Номер телефона",
  phonePlaceholder: "6X XXX XXXX",
  sendCode: "Получить код",
  codeTitle: "Введите код",
  codeSubtitle: (phone) => `Отправили код из шести цифр на ${phone}.`,
  codeSubtitleMessenger: (app, phone) => `Отправили код из шести цифр в ${app} на ${phone}.`,
  codeBySms: 'Получить код по SMS',
  codeHint: "Код действует 5 минут.",
  resend: "Отправить ещё раз",
  resendIn: (seconds) => `Новый код через ${seconds} с`,
  otpThrottledFor: (minutes) => `Новый код можно запросить через ${minutes} мин.`,
  changeNumber: "Другой номер",
  legalNote: (terms, privacy) => `Входя, вы принимаете [условия использования](${terms}) и [политику конфиденциальности](${privacy}).`,
  chooseAddress: "Выберите адрес",
  newAddress: "Новый адрес",
  searchAddressPlaceholder: "Улица и номер дома",
  noAddresses: "Адресов пока нет",
  noAddressesHint: "Добавьте адрес, и мы покажем, что можем привезти.",
  entranceComment: "Как найти вход",
  entranceCommentPlaceholder: "Код калитки, ориентир",
  addressEntrance: "Подъезд",
  addressFloor: "Этаж",
  addressApartment: "Квартира",
  addressIntercom: "Домофон",
  deliverHere: "Привезти сюда",
  outOfZone: "Сюда пока не возим",
  notDeliverable: "Вне зоны доставки",
  outOfZoneHint: (address) => `По адресу ${address} пока не доставляем. Выберите другой адрес или добавьте новый.`,
  changeAddress: "Сменить адрес",
  coverageRequest: "Сообщите, когда начнёте",
  coverageRequested: "Сообщим",
  locateMe: "Где я",
  dragPinHint: "Подвиньте карту так, чтобы метка встала на вход.",
  myAddresses: "Адреса",
  a11yAddFavorite: "В избранное",
  a11yRemoveFavorite: "Убрать из избранного",
  a11yAddToCart: "В корзину",
  a11yQuantity: "Количество",
  a11yIncrease: "Ещё один",
  a11yDecrease: "На один меньше",
  a11yRemoveFromCart: "Убрать из корзины",
  a11yNotifications: (unread) => unread > 0 ? `Уведомления, непрочитанных: ${unread}` : "Уведомления",
  a11ySendMessage: "Отправить сообщение",
  a11yClearText: "Очистить",
  a11yProductInfo: "О товаре",
  share: "Поделиться",
  a11yStars: (a) => `${a} из 5 звёзд`,
  openUntil: (closesAt, eta) => `Открыто до ${closesAt} · ${eta} мин`,
  closedOpensAt: (opensAt) => `Закрыто · откроемся в ${opensAt}`,
  storeClosedTitle: "Сейчас закрыто",
  storeClosedHint: (opensAt) => `Корзину можно собрать сейчас, а оформить заказ в ${opensAt}, когда откроемся.`,
  storePausedLine: "Пауза · много заказов",
  storePausedTitle: "Небольшая пауза: не успеваем",
  storePausedHint: "Заказов сейчас больше, чем мы можем довезти. Соберите корзину, оформление откроется, как только разгребём.",
  storeDelayedTitle: "Много заказов, везём дольше",
  storeDelayedHint: (extra) => `Заказов больше обычного, доставка займёт примерно на ${durationRu(extra)} дольше. Спасибо, что ждёте.`,
  orderAgain: "Заказать снова",
  addAllToCart: "Всё в корзину",
  addedToCart: "В корзине",
  youBought: "Вы покупали",
  homeProducts: "Товары",
  homeSoldOut: "Всё раскупили",
  homeSoldOutHint: "Уже везём новые товары",
  purchasesEmptyHint: "Здесь будут ваши покупки, чтобы повторить заказ в одно касание.",
  deals: "Скидки",
  exploreDeals: "Все скидки",
  mostOrdered: "Часто заказывают",
  newItems: "Новинки",
  badgeNew: "НОВИНКА",
  badgeAge: "18+",
  ageGateTitle: "Сколько вам лет?",
  ageGateText: "Алкоголь продаём только совершеннолетним. Укажите дату рождения, при вручении курьер попросит документ.",
  ageGateConfirm: "Подтвердить",
  ageGateUnderage: "Товары 18+ продаём только совершеннолетним.",
  birthDate: "Дата рождения",
  birthDateHint: "Нужна для покупки товаров 18+.",
  emailForReceipts: "E-mail для чеков",
  emailForReceiptsHint: "Пришлём подтверждение оплаты и фискальный чек.",
  ageRestrictedOrder: "В заказе товар 18+: курьер попросит документ",
  itemsCount: (count) => russianCount(count, "товар", "товара", "товаров"),
  searchProducts: "Найти продукты",
  nothingFound: "Ничего не нашлось",
  nothingFoundHint: "Проверьте написание или загляните в другие категории.",
  resultsCount: (count) => russianCount(count, "товар", "товара", "товаров"),
  categoryEmptyHint: "Здесь пока пусто. Загляните в соседнюю категорию.",
  outOfStock: "Закончился",
  onlyLeft: (qty) => `осталось ${qty}`,
  addToCart: "В корзину",
  goToCart: "К корзине",
  composition: "Состав",
  nutrition: "Пищевая ценность",
  per100g: "на 100 г",
  per100ml: "на 100 мл",
  energyLabel: "Калорийность",
  proteinLabel: "Белки",
  fatLabel: "Жиры",
  carbsLabel: "Углеводы",
  kcalUnit: "ккал",
  gramUnit: "г",
  proteinShort: "Б",
  fatShort: "Ж",
  carbsShort: "У",
  nutritionWhole: "в одной штуке",
  nutritionAmount: (weight) => `на ${weight}`,
  nutritionPartial: "У части товаров нет данных, итог приблизительный.",
  tagVegan: "Веганское",
  tagGlutenFree: "Без глютена",
  tagSugarFree: "Без сахара",
  productInfo: "О товаре",
  readMore: "Подробнее",
  gtin: "GTIN",
  countryOfOrigin: "Страна производства",
  allergens: "Аллергены",
  additives: "Добавки",
  shelfLife: "Срок годности",
  shelfLifeValue: (days) => russianDuration(days),
  storage: "Хранение",
  promoUntil: (day) => `Скидка до ${day}, пока товар в наличии.`,
  oftenBoughtWith: "Вместе вкуснее",
  youMightLike: "Похожие товары",
  cartRecommendations: "Ничего не забыли?",
  report: "Сообщить о проблеме",
  reportReason: (code) => {
      switch (code) {
        case "wrong_info": return "Ошибка в описании или цене";
        case "quality": return "Плохое качество";
        case "missing": return "Чего-то не хватает";
        default: return "Другое";
      }
  },
  reportPlaceholder: "Расскажите, что не так",
  reportSend: "Отправить",
  reportSent: "Спасибо, мы разберёмся",
  favoritesTitle: "Избранное",
  favoritesEmpty: "В избранном пусто",
  favoritesEmptyHint: "Нажмите на сердечко у товара, и он появится здесь.",
  cartTitle: "Корзина",
  cartEmpty: "В корзине пусто",
  cartEmptyHint: "Загляните в каталог и выберите что-нибудь.",
  backToCatalog: "В каталог",
  itemsTotal: "Товары",
  deliveryFee: "Доставка",
  deliveryFree: "Бесплатно",
  deliveryFreeUnlocked: "Доставка бесплатная",
  deliveryTime: "Привезём за",
  etaAbout: (minutes) => `≈ ${minutes} мин`,
  freeFrom: (amount) => `Бесплатная доставка от ${amount}`,
  addMoreForFreeDelivery: (amount) => `Ещё ${amount}, и доставка бесплатно`,
  addMoreToOrder: (amount) => `До минимального заказа не хватает ${amount}`,
  orderTooHeavy: (weight, limit) => `Заказ слишком тяжёлый: ${weight}, максимум ${limit}`,
  toFreeDeliveryShort: (amount) => `${amount} до бесплатной доставки`,
  toMinimumShort: (amount) => `${amount} до минимального заказа`,
  total: "Итого",
  checkout: "Оформить",
  clearCart: "Очистить корзину",
  itemGone: "закончился",
  itemLeft: (qty) => `осталось ${qty}`,
  cartUpdated: "Корзина изменилась",
  cartUpdatedHint: "Пока вы выбирали, часть товаров разобрали. Проверьте корзину и оформите заказ ещё раз.",
  fixCart: "Обновить корзину",
  checkAgain: "Заказать снова",
  checkoutTitle: "Заказ",
  deliveryTo: "Адрес доставки",
  commentToCourier: "Комментарий курьеру",
  commentPlaceholder: "Позвоните, когда будете у двери",
  leaveAtDoor: "Оставить у двери",
  leaveAtDoorHint: "Курьер не будет звонить.",
  leaveAtDoorBlocked: "В корзине товар 18+: курьер проверит документ и отдаст заказ в руки.",
  alcoholClosedHint: (from, until) => `Товары 18+ продаём с ${from} до ${until}. Уберите их из корзины или оформите заказ позже.`,
  testCard: "Тестовая карта",
  payAmount: (amount) => `Оплатить ${amount}`,
  payWith: "Оплатить с",
  checkoutLegalNote: (url) => `Оформляя заказ, вы принимаете [условия использования](${url}).`,
  paying: "Оплачиваем…",
  paymentCardTitle: "Платёжная карта",
  vatIncluded: "НДС включён в цену.",
  consentTerms: (url) => `Я ознакомлен с [условиями использования](${url}) и принимаю их.`,
  cardsAccepted: "Принимаем",
  paymentChecking: "Проверяем оплату…",
  paymentConfirmed: "Оплата прошла, заказ принят.",
  paymentStillChecking: "Банк ещё не ответил. Статус заказа обновится сам.",
  cardNumber: "Номер карты",
  cardExpiry: "Срок действия",
  cardOwner: "Имя на карте",
  newCard: "Новая карта",
  paymentMethods: "Способы оплаты",
  noCardsHint: "Добавьте карту, чтобы платить в одно касание.",
  addCard: "Добавить карту",
  saveCard: "Сохранить карту",
  cardSaved: "Карта сохранена",
  cardCheckHint: "Банк заблокирует 1,00 RSD для проверки карты и сразу разблокирует.",
  cardStillChecking: "Банк ещё не ответил. Карта появится в списке, как только ответит.",
  defaultCardHint: "Карта с галочкой предлагается при оплате.",
  deleteCard: "Удалить",
  deleteCardTitle: "Удалить карту?",
  deleteCardText: "В следующий раз данные карты нужно будет ввести заново.",
  cardDeleted: "Карта удалена",
  edit: "Изменить",
  done: "Готово",
  paidWithCard: "Оплачено картой",
  paymentInProgress: "Идёт оплата…",
  paymentDone: "Оплачено!",
  paymentProblem: "Проблема с оплатой, нужна ваша помощь",
  payOtherWay: "Оплатить иначе",
  promoCode: "Промокод",
  promoCodePlaceholder: "Введите код",
  promoApply: "Применить",
  promoApplied: (amount) => `Промокод применён: −${amount}`,
  promoRemove: "Убрать",
  discount: "Скидка",
  gift: "Подарок",
  giftLine: (name) => `Подарок: ${name}`,
  promoReason: (reason) => {
      switch (reason) {
        case "not_found": return "Такого промокода нет.";
        case "inactive": case "expired": return "Промокод больше не действует.";
        case "not_started": return "Промокод ещё не начал действовать.";
        case "min_order": return "Для этого промокода нужна корзина побольше.";
        case "limit_reached": return "Промокод уже использовали максимум раз.";
        case "client_limit": return "Вы уже использовали этот промокод.";
        case "first_order_only": return "Промокод только для первого заказа.";
        default: return "Промокод не подошёл.";
      }
  },
  partnerPromoSubtitle: "Квартиры на берегу Савы. Рассрочка 0% — 7 платежей на время стройки.",
  partnerPromoCta: "Смотреть квартиры",
  reserveLeft: (time) => `Товары забронированы ещё на ${time}`,
  reserveExpired: "Бронь истекла",
  deliveryCodeTitle: "Код вручения",
  deliveryCodeHint: "Назовите курьеру, когда он принесёт заказ.",
  codePromptTitle: "Курьер у двери",
  codePromptHint: "Назовите курьеру этот код",
  codePromptDone: "Понятно",
  notificationDeliveryCode: "Курьер у двери",
  notificationDeliveryCodeHint: "Назовите ему код вручения",
  pushDeliveryCode: "Курьер у двери",
  pushDeliveryCodeBody: (code) => `Код для курьера: ${code}`,
  openOrder: "Открыть заказ",
  leaveAtDoorOrder: "Оставим у двери",
  orderItems: "Товары",
  receipt: "Чек",
  openReceipt: "Открыть чек",
  courierName: "Курьер",
  courierRunAhead: (count) => count === 1 ? "Сначала завезёт ещё один заказ, потом ваш" : `Сначала завезёт ещё ${russianCount(count, "заказ", "заказа", "заказов")}, потом ваш`,
  courierRunFirst: (others) => others === 1 ? "Ваш заказ первый, у курьера с собой ещё один" : `Ваш заказ первый, у курьера с собой ещё ${others}`,
  mapExpand: "Карта на весь экран",
  mapLocate: "Показать курьера и адрес",
  cancelOrder: "Отменить заказ",
  cancelOrderConfirm: "Отменить заказ?",
  cancelReasonPlaceholder: "Почему отменяете",
  cancelNeedsOperator: "Заказ уже собирают. Отменить его может поддержка.",
  cancelNeedsOperatorOnTheWay: "Заказ уже везут. Отменить его может поддержка.",
  noOrders: "Заказов пока нет",
  noOrdersHint: "Первый приедет минут за двадцать.",
  arrivesAround: (time) => `Привезём около ${time}`,
  arrivesSoon: "Уже скоро",
  activeOrder: "Сейчас",
  historyTitle: "Раньше",
  deliveredCongrats: "Приятного аппетита!",
  deliveredBy: (courier) => `Курьер ${courier}`,
  rateOrderTitle: "Как вам заказ?",
  tipDeclined: "Карта отклонила чаевые. Оценка сохранена: попробуйте ещё раз или уменьшите сумму.",
  rateOrderHint: "Оцените доставку и, если хотите, оставьте курьеру на чай",
  thanksForRating: "Спасибо за оценку",
  tipsTitle: "Курьеру на чай",
  tipsHint: "Все чаевые получает курьер",
  noTipsShort: "без",
  tipOther: "Другая сумма",
  ratingCommentPlaceholder: "Комментарий, если хотите",
  supportTitle: "Поддержка",
  supportOrder: "Вопрос по заказу",
  supportEmpty: "Напишите нам",
  supportEmptyHint: "Отвечаем здесь же, обычно за несколько минут.",
  supportPlaceholder: "Сообщение",
  supportStaff: "Поддержка",
  bonusBalance: "Бонусы",
  bonusUsed: "Бонусы",
  bonusOnAccount: (amount) => `На счёте ${amount}`,
  bonusHint: "Бонусы сами списываются при следующем заказе.",
  notificationsTitle: "Уведомления",
  notificationsEmpty: "Уведомлений пока нет",
  notificationsEmptyHint: "Здесь будут новости о заказах и скидки.",
  notifyOrders: "О заказах",
  notifyOrdersHint: "Собрали, везём, доставили",
  notifyPromo: "Скидки и подборки",
  notifyPromoHint: "Скидки на то, что вы часто берёте",
  notificationSupportReply: "Ответ поддержки",
  notificationCredit: (a) => `Вам начислили ${a} бонусами`,
  notificationRedelivery: "Везём заказ заново",
  notificationRedeliveryHint: "Бесплатно, простите за накладку",
  notificationZoneOpen: "Теперь доставляем и по вашему адресу",
  creditReasonOrderCancelled: "Вернули бонусы отменённого заказа",
  creditReasonOrderAdjusted: "Заказ пересчитан, бонусы вернули",
  notificationZoneOpenHint: "Можно заказывать",
  notificationShortPick: "Не всё нашлось из заказа",
  notificationShortPickRefund: (a) => `Вернули ${a} на карту`,
  itemMissing: (a) => `не нашлось: ${a}`,
  refundedToCard: "Возвращено на карту",
  chargedTotal: "Итого списано",
  notificationReturnRefund: "Возврат части заказа",
  itemReturned: (a) => `вернули: ${a}`,
  addToOrder: (a) => `Добавить к заказу · ${a}`,
  supplementTitle: (a) => `Заказ · ${a}`,
  supplementCharge: "Доплата",
  supplementNewTotal: "Новая сумма заказа",
  supplementPay: (a) => `Доплатить ${a}`,
  supplementAdd: "Добавить без доплаты",
  supplementPicked: "Заказ уже собран. Оформите новый заказ.",
  supplementDeclined: "Карта отклонила доплату, заказ остался прежним",
  supplementDone: "Добавили к заказу",
  editProfile: "Профиль",
  yourName: "Имя",
  yourNamePlaceholder: "Как к вам обращаться",
  nameOptionalHint: "Необязательно. Имя увидит курьер.",
  changePhoto: "Сменить фото",
  removePhoto: "Убрать фото",
  save: "Сохранить",
  language: "Язык",
  signOut: "Выйти",
  signOutConfirm: "Выйти из аккаунта на этом устройстве?",
  sessionExpired: "Нужно войти заново.",
  deleteAccount: "Удалить аккаунт",
  deleteAccountConfirm: "Аккаунт удалится сразу и навсегда. Заказы и чеки мы обязаны хранить по закону.",
  addAddressForDelivery: "Добавить адрес доставки",
  addAddress: "Добавить адрес",
  signIn: "Войти",
  signInToContinue: "Войдите, чтобы продолжить",
  guestProfileHint: "Заказы, избранное и адреса привязаны к номеру телефона. Код для входа придёт по SMS или в мессенджер.",
  addressGateTitle: "Куда привезти?",
  addressGateText: "От адреса зависят наличие товаров и цена доставки, поэтому спрашиваем его сразу.",
  pushPrimerTitle: "Скажем, когда курьер выйдет",
  pushPrimerText: "Статус заказа и код вручения придут уведомлением. Скидки: только если включите их в профиле.",
  pushPrimerAllow: "Включить уведомления",
  pushPrimerLater: "Позже",
  orDivider: "или",
  phoneForCourierTitle: "Телефон для курьера",
  phoneForCourierText: "Курьер позвонит, если не найдёт вход. Можно не указывать.",
  phoneOptional: "Необязательно",
  payment: "Оплата",
  productInfoMissing: "Состав пока не указан.",
  amountMore: (amount) => `ещё ${amount}`,
  privacyPolicy: "Политика конфиденциальности",
  termsOfUse: "Условия использования",
  statuses: {
    "PAYMENT_PENDING": "Ждёт оплаты",
    "PAID": "Собираем",
    "PICKING": "Собираем",
    "PICKED": "Собран",
    "IN_TRANSIT": "В пути",
    "DELIVERED": "Доставлен",
    "CANCELLED": "Отменён",
    "CANCELLED_LATE": "Отменён",
    "RETURN": "Возврат",
    "REFUNDED": "Деньги возвращены",
  },
  statusesShort: {
    "PAYMENT_PENDING": "Ждёт оплаты",
    "IN_TRANSIT": "В пути",
  },
  errors: {
    "offline": "Нет интернета.",
    "malformed_response": "Не удалось разобрать ответ сервера.",
    "invalid_phone": "Проверьте номер телефона.",
    "otp_invalid": "Код не подошёл или устарел.",
    "otp_throttled": "Новый код можно запросить чуть позже.",
    "otp_attempts_exceeded": "Слишком много попыток. Запросите новый код.",
    "validation_failed": "Проверьте введённые данные.",
    "address_required": "Выберите адрес доставки.",
    "address_out_of_zone": "По этому адресу пока не возим.",
    "address_not_found": "Адрес не найден.",
    "point_required": "Отметьте точку на карте.",
    "product_unavailable": "Один из товаров сняли с продажи.",
    "insufficient_stock": "Столько на складе нет.",
    "supplement_pending": "Доплата за добавленное раньше ещё проходит.",
    "promo_code_invalid": "Промокод не подошёл.",
    "account_has_active_orders": "У вас есть заказ в работе. Дождитесь доставки или отмените его.",
    "order_too_small": "Сумма заказа меньше минимальной.",
    "order_limit": "Заказ слишком большой: уменьшите количество или разделите на два заказа.",
    "sms_failed": "SMS не отправилось. Попробуйте ещё раз.",
    "rate_limited": "Слишком много попыток. Подождите немного.",
    "empty_order": "Корзина пуста.",
    "order_not_found": "Заказ не найден.",
    "order_state": "Заказ уже перешёл дальше, это действие недоступно.",
    "invalid_transition": "Заказ уже перешёл дальше, это действие недоступно.",
    "cancel_needs_operator": "Заказ уже собирают. Напишите в поддержку.",
    "store_closed": "Сейчас закрыто. Корзина дождётся открытия.",
    "store_paused": "Приём заказов на паузе: не успеваем довезти. Попробуйте чуть позже.",
    "age_restricted": "Товары 18+ продаём только совершеннолетним.",
    "leave_at_door_not_allowed": "Заказ с товаром 18+ нельзя оставить у двери.",
    "alcohol_sale_closed": "Товары 18+ сейчас не продаются.",
    "birth_date_invalid": "Проверьте дату рождения.",
    "email_invalid": "Проверьте e-mail.",
    "call_in_progress": "Ещё обрабатываем. Попробуйте через минуту.",
    "payment_declined": "Оплата не прошла.",
    "collection_not_found": "Этой подборки больше нет.",
    "product_not_found": "Товар не найден.",
    "forbidden": "Нет доступа.",
    "internal": "Ошибка на сервере. Попробуйте ещё раз.",
  },
  pushOrder: {
    "PAID": "Собираем заказ",
    "PICKING": "Собираем заказ",
    "IN_TRANSIT": "Курьер уже в пути",
    "DELIVERED": "Заказ доставлен. Как всё прошло?",
    "CANCELLED": "Заказ отменён",
    "CANCELLED_LATE": "Заказ отменён",
    "REFUNDED": "Деньги за заказ вернулись",
  },
  pushSupport: "Ответ поддержки",
  pushRedelivery: (orderNumber) => `Везём заказ №${orderNumber} заново и бесплатно`,
  pushZoneOpen: "Теперь доставляем и по вашему адресу — можно заказывать",
  pushShortPick: (orderNumber, amount) => `Заказ №${orderNumber}: не всё нашлось, возвращаем ${amount} на карту`,
  pushReturnRefund: (orderNumber, amount) => `Заказ №${orderNumber}: возвращаем ${amount} на карту за возвращённый товар`,
};

export const STRINGS: Record<Lang, Strings> = { sr, en, ru };
